import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useId, useMemo, useRef, useState, type ReactNode } from 'react';
import { ApiError } from '@crave/api-client';
import { Spicy } from '@crave/ui';
import { api } from '../api.ts';
import { QrCode } from '../components/QrCode.tsx';

/**
 * The point of sale (#55): take a walk-up order on the iPad or iPhone, charge it on the card reader
 * at this location or text the customer a pay link, and send it to the kitchen once paid.
 */

interface MenuItem {
  id: string;
  name: string;
  category: string;
  price_cents: number | null;
  orderable: boolean;
  sold_out: boolean;
  unavailable: boolean;
  spicy?: boolean;
  modifiers: {
    id: string;
    name: string;
    kind: string;
    price_delta_cents: number;
    makes_spicy?: boolean;
  }[];
}
interface Setup {
  location: { id: string; slug: string; name: string };
  pos_enabled: boolean;
  /** A walk-up can be started now even on a day with no online pickup times. */
  walk_up_now?: boolean;
  ready: boolean;
  payments: { connected: boolean; test_mode: boolean; texting: boolean };
  readers: { id: string; label: string; status: string | null; simulated: boolean }[];
  slots: { id: string; label: string; remaining: number; now: boolean }[];
}
interface Quote {
  totals: {
    subtotal_cents: number;
    tax_cents: number;
    tip_cents: number;
    total_cents: number;
    discount_cents: number;
    credit_applied_cents: number;
    amount_due_cents: number;
  };
  credit_available_cents: number;
  birthday: { item_name: string | null; discount_cents: number } | null;
}
interface FoundCustomer {
  id: string;
  name: string | null;
  email_hint: string;
  phone: string | null;
  has_account: boolean;
  credit_cents: number;
  birthday: { item_name: string | null; until: string } | null;
}
interface Lookup {
  loyalty: { earn_rate_bps: number | null; min_order_cents: number };
  items: FoundCustomer[];
}
interface PosStatus {
  order_id: string;
  order_number: string;
  status: string;
  paid: boolean;
  canceled: boolean;
  customer_name: string | null;
  total_cents: number;
  collected_by: string | null;
  last_error: string | null;
  reader: { id: string; label: string } | null;
  reader_action: { status: string; failureMessage: string | null } | null;
  reader_problem?: string | null;
  pay_url?: string;
  pay_phone?: string | null;
  text?: { status: string; error: string | null } | null;
  pos?: string | null;
  taken_by?: string | null;
  /** A paid walk-up a manager can still void before it is handed over. */
  can_void?: boolean;
}
interface PosDevice {
  id: string;
  number: number;
  name: string | null;
  label: string;
  token?: string;
}
interface Recent {
  pos_number: number | null;
  taken_by: string | null;
  id: string;
  order_number: string;
  status: string;
  total_cents: number;
  customer_name: string | null;
  time_label: string;
  paid: boolean;
}
interface Line {
  key: string;
  item: MenuItem;
  quantity: number;
  modifierIds: string[];
  notes: string;
}

const money = (cents: number) => `$${(cents / 100).toFixed(2)}`;
const TIPS = [0, 10, 15, 20] as const;
const ACTIVE_KEY = (slug: string) => `crave.pos.active.${slug}`;
const READER_KEY = 'crave.pos.reader';
/** The secret this iPad got when it registered as a POS. */
const DEVICE_KEY = 'crave.pos.deviceToken';
/** The order being rung up, kept on the device so a reload (or a dropped tab) never loses it. */
const DRAFT_KEY = (slug: string) => `crave.pos.draft.${slug}`;

interface Draft {
  lines: { itemId: string; quantity: number; modifierIds: string[]; notes: string }[];
  name: string;
  phone: string;
  textUpdates: boolean;
  tipPct: number;
  slotId: string | null;
  customer: FoundCustomer | null;
  earnRate: number | null;
  useCredit: boolean;
  useBirthday: boolean;
}

function readDraft(slug: string): Draft | null {
  try {
    const raw = localStorage.getItem(DRAFT_KEY(slug));
    if (!raw) return null;
    const d = JSON.parse(raw) as Draft;
    return Array.isArray(d.lines) ? d : null;
  } catch {
    return null;
  }
}

/**
 * This iPad as a POS: registers it (a random POS number) the first time, and tells the API who is
 * using it and where every time the POS opens.
 */
function usePosDevice(location: string) {
  return useQuery({
    queryKey: ['pos-device', location],
    queryFn: async () => {
      const token = store.get(DEVICE_KEY);
      const r = await api.post<PosDevice>('/staff/pos/device', {
        location,
        ...(token ? { token } : {}),
      });
      if (r.data.token) store.set(DEVICE_KEY, r.data.token);
      return r.data;
    },
    staleTime: 10 * 60_000,
    retry: 2,
  });
}

const store = {
  get(key: string): string | null {
    try {
      return localStorage.getItem(key);
    } catch {
      return null;
    }
  },
  set(key: string, value: string | null) {
    try {
      if (value === null) localStorage.removeItem(key);
      else localStorage.setItem(key, value);
    } catch {
      // Private mode: kept for this visit only.
    }
  },
};

const errorText = (err: unknown) =>
  err instanceof ApiError ? err.message : 'Something went wrong. Check the connection.';

const CSS = `
.pos { max-width: 1240px; margin: 0 auto; padding: 18px 20px 20px; display: grid; gap: 22px;
  grid-template-columns: minmax(0, 1fr) 400px; align-items: stretch; height: 100%; overflow: hidden; }
/* Each column scrolls on its own, so the menu stays put while the ticket grows. */
.pos-col { display: grid; gap: 18px; min-width: 0; min-height: 0; align-content: start;
  overflow-y: auto; overscroll-behavior: contain; padding-bottom: 4px; }
.pos-head { display: flex; align-items: baseline; justify-content: space-between; gap: 8px 16px; flex-wrap: wrap; }
.pos-who { display: inline-flex; align-items: center; min-height: 32px; padding: 0 12px; border-radius: 999px;
  background: var(--mist); font-size: 15px; font-weight: 600; color: var(--ink-soft); }
.pos-title { margin: 0; font-size: 28px; line-height: 1.15; letter-spacing: -.01em; }
.pos-caption { margin: 0 0 -8px; padding: 0 4px; font-size: 13px; font-weight: 600; letter-spacing: .04em;
  text-transform: uppercase; color: var(--ink-soft); }
.pos-menu { display: grid; grid-template-columns: repeat(auto-fill, minmax(184px, 1fr)); gap: 14px; }
.pos-item { display: grid; gap: 6px; align-content: space-between; min-height: 96px; padding: 16px 18px; text-align: left;
  border: 0; border-radius: 18px; background: var(--paper); color: var(--ink); cursor: pointer;
  box-shadow: 0 1px 2px rgba(20,20,20,.05), 0 0 0 1px rgba(20,20,20,.06); font: inherit;
  transition: transform .12s ease, box-shadow .12s ease; }
.pos-item:active { transform: scale(.97); box-shadow: 0 0 0 1px rgba(20,20,20,.1); }
.pos-item strong { font-size: 20px; line-height: 1.2; letter-spacing: -.01em; }
.pos-item span { color: var(--ink-soft); font-weight: 600; font-variant-numeric: tabular-nums; }
.pos-item:disabled { opacity: .5; cursor: default; transform: none; }
.pos-tag { justify-self: start; font-size: 12px; font-weight: 700; letter-spacing: .02em; text-transform: uppercase;
  padding: 2px 8px; border-radius: 999px; background: var(--mist); color: var(--ink-soft); }
.pos-card { background: var(--paper); border-radius: 18px; box-shadow: 0 1px 2px rgba(20,20,20,.05), 0 0 0 1px rgba(20,20,20,.06); }
.pos-ticket { display: flex; flex-direction: column; gap: 14px; padding: 18px; min-height: 0; height: 100%;
  overflow-y: auto; overscroll-behavior: contain; }
/* What was ordered stays at the top and the pay buttons at the foot, whatever the window height;
   only what is between them scrolls. */
.pos-ticket-head { position: sticky; top: -18px; z-index: 2; flex: none; margin: -18px -18px 0;
  padding: 18px 18px 8px; background: var(--paper); display: grid; gap: 10px; align-content: start; }
.pos-ticket-scroll { flex: none; display: grid; gap: 12px; align-content: start; }
.pos-lines { display: grid; }
.pos-line { display: grid; grid-template-columns: minmax(0, 1fr) auto; gap: 2px 12px; padding: 10px 0; border-top: 1px solid var(--line); }
.pos-line:first-child { border-top: 0; }
.pos-line b { font-size: 17px; }
.pos-line small { grid-column: 1; color: var(--ink-soft); font-size: 14px; }
.pos-line-actions { grid-column: 1 / -1; display: flex; gap: 8px; align-items: center; }
.pos-step { display: inline-flex; align-items: center; border-radius: 12px; background: var(--mist); }
.pos-step button { width: 44px; height: 40px; border: 0; background: none; font-size: 22px; font-weight: 600; cursor: pointer; color: var(--ink); }
.pos-step span { min-width: 28px; text-align: center; font-weight: 700; }
.pos-link { border: 0; background: none; color: var(--crave-red); font: inherit; font-weight: 600; min-height: 40px; padding: 0 8px; cursor: pointer; }
.pos-seg { display: flex; flex-wrap: wrap; gap: 6px; }
.pos-chip { min-height: 44px; padding: 0 14px; border-radius: 12px; border: 1px solid var(--line); background: var(--paper);
  font: inherit; font-weight: 600; color: var(--ink); cursor: pointer; }
.pos-chip[aria-pressed='true'] { background: var(--ink); border-color: var(--ink); color: #fff; }
.pos-chip:disabled { opacity: .4; cursor: default; }
.pos-chip small { font-weight: 500; opacity: .7; margin-left: 6px; }
.pos-seg .pos-chip { padding: 0 12px; }
.pos-tips { display: grid; grid-template-columns: repeat(4, minmax(0, 1fr)); gap: 6px; }
.pos-tips .pos-chip { display: grid; place-content: center; padding: 4px 4px; line-height: 1.15; }
.pos-tips .pos-chip small { margin: 0; font-size: 12px; }
.pos-field { display: grid; gap: 4px; font-size: 14px; font-weight: 600; color: var(--ink-soft); }
.pos-input { min-height: 48px; width: 100%; min-width: 0; padding: 0 14px; border-radius: 12px; border: 1px solid var(--line);
  background: var(--paper); color: var(--ink); font: inherit; font-size: 17px; }
.pos-check { display: flex; align-items: center; gap: 10px; min-height: 44px; font-weight: 600; }
.pos-check input { width: 22px; height: 22px; accent-color: var(--crave-red); }
.pos-totals { display: grid; gap: 4px; padding-top: 10px; border-top: 1px solid var(--line); font-variant-numeric: tabular-nums; }
.pos-totals div { display: flex; justify-content: space-between; color: var(--ink-soft); }
.pos-totals .pos-total { color: var(--ink); font-size: 24px; font-weight: 800; margin-top: 4px; }
.pos-pay { display: grid; gap: 10px; }
.pos-cust { display: grid; gap: 8px; padding: 12px 14px; border-radius: 14px; background: var(--mist); }
.pos-ticket-foot { position: sticky; bottom: -18px; z-index: 1; flex: none; display: grid; gap: 12px;
  margin: auto -18px -18px; padding: 14px 18px 18px; background: var(--paper); border-top: 1px solid var(--line); }
.pos-btn { min-height: 60px; border: 0; border-radius: 14px; font: inherit; font-size: 19px; font-weight: 700; cursor: pointer;
  background: var(--crave-red); color: #fff; padding: 0 18px; }
.pos-btn[data-kind='secondary'] { background: var(--mist); color: var(--ink); }
.pos-btn[data-kind='plain'] { background: transparent; color: var(--crave-red); min-height: 48px; font-size: 17px; }
.pos-btn:disabled { opacity: .45; cursor: default; }
.pos-note { margin: 0; color: var(--ink-soft); font-size: 14px; line-height: 1.4; }
.pos-error { margin: 0; padding: 10px 14px; border-radius: 12px; background: #fdecec; color: var(--crave-red); font-weight: 600; }
.pos-recent { display: grid; }
.pos-recent button { display: grid; grid-template-columns: auto minmax(0, 1fr) auto; gap: 12px; align-items: center; min-height: 56px;
  padding: 8px 16px; border: 0; border-top: 1px solid var(--line); background: none; font: inherit; color: var(--ink); text-align: left; cursor: pointer; }
.pos-recent button:first-child { border-top: 0; }
.pos-pill { font-size: 13px; font-weight: 700; padding: 3px 10px; border-radius: 999px; background: var(--mist); color: var(--ink-soft); }
.pos-pill[data-tone='ok'] { background: #e7f2eb; color: var(--ok); }
.pos-pill[data-tone='wait'] { background: #fbf0dc; color: #875710; }
.pos-bar { display: none; }

.pos-sheet { border: 0; padding: 0; margin: auto; width: min(560px, calc(100vw - 24px)); max-height: calc(100dvh - 24px);
  border-radius: 22px; background: var(--ivory); color: var(--ink); box-shadow: 0 24px 60px rgba(20,20,20,.3); overflow: auto; }
.pos-sheet::backdrop { background: rgba(20,20,20,.5); }
.pos-sheet-body { display: grid; gap: 18px; padding: 22px; }
.pos-sheet-head { display: flex; justify-content: space-between; gap: 12px; align-items: flex-start; }
.pos-sheet-head h2 { margin: 0; font-size: 24px; line-height: 1.2; }
.pos-mods { display: grid; }
.pos-mod { display: flex; align-items: center; justify-content: space-between; gap: 12px; min-height: 56px; padding: 0 16px;
  border-top: 1px solid var(--line); font-size: 17px; font-weight: 600; cursor: pointer; }
.pos-mod:first-child { border-top: 0; }
.pos-mod > span:first-child { display: grid; gap: 2px; }
.pos-mod input { width: 24px; height: 24px; accent-color: var(--crave-red); }
.pos-status { display: grid; justify-items: center; gap: 10px; text-align: center; padding: 8px 0; }
.pos-amount { font-size: 48px; font-weight: 800; letter-spacing: -.02em; line-height: 1; }
.pos-state { font-size: 20px; font-weight: 700; }
.pos-spinner { width: 44px; height: 44px; border-radius: 50%; border: 4px solid var(--line); border-top-color: var(--crave-red);
  animation: pos-spin 0.9s linear infinite; }
@keyframes pos-spin { to { transform: rotate(360deg); } }
.pos-done { width: 72px; height: 72px; border-radius: 50%; background: var(--ok); display: grid; place-items: center; }
.pos-test { display: flex; gap: 8px; justify-content: center; flex-wrap: wrap; padding: 10px; border-radius: 14px;
  border: 1px dashed var(--line); }

@media (max-width: 900px) {
  /* One column on a phone: the page scrolls, the columns do not. */
  .pos { grid-template-columns: minmax(0, 1fr); height: auto; overflow: visible; padding-bottom: 96px; }
  .pos-col { overflow: visible; }
  .pos-ticket { height: auto; overflow: visible; }
  .pos-ticket-head { position: static; margin: 0; padding: 0; }
  .pos-ticket-foot { position: static; }
  .pos-ticket-foot { margin: 0; padding: 14px 0 0; }
  .pos-bar { display: flex; position: fixed; left: 12px; right: 12px; bottom: calc(12px + env(safe-area-inset-bottom)); z-index: 20; }
  .pos-bar .pos-btn { flex: 1; box-shadow: 0 10px 30px rgba(20,20,20,.25); }
}
@media (prefers-reduced-motion: reduce) { .pos-spinner { animation-duration: 3s; } .pos-item:active { transform: none; } }
`;

function Sheet({
  open,
  title,
  onClose,
  closeLabel = 'Close',
  children,
}: {
  open: boolean;
  title: string;
  onClose: (() => void) | null;
  closeLabel?: string;
  children: ReactNode;
}) {
  const ref = useRef<HTMLDialogElement>(null);
  const titleId = useId();
  useEffect(() => {
    const d = ref.current;
    if (!d) return;
    if (open && !d.open) d.showModal();
    if (!open && d.open) d.close();
  }, [open]);
  return (
    <dialog
      ref={ref}
      className="pos-sheet"
      aria-labelledby={titleId}
      onCancel={(e) => {
        e.preventDefault();
        onClose?.();
      }}
    >
      {open && (
        <div className="pos-sheet-body">
          <div className="pos-sheet-head">
            <h2 id={titleId}>{title}</h2>
            {onClose && (
              <button type="button" className="pos-link" onClick={onClose}>
                {closeLabel}
              </button>
            )}
          </div>
          {children}
        </div>
      )}
    </dialog>
  );
}

export function Pos({ location, staffName }: { location: string; staffName: string | null }) {
  const qc = useQueryClient();
  const device = usePosDevice(location);
  const setup = useQuery({
    queryKey: ['pos-setup', location],
    queryFn: async () => (await api.get<Setup>(`/staff/pos/setup?location=${location}`)).data,
    refetchInterval: 30_000,
  });
  const menu = useQuery({
    queryKey: ['pos-menu', location],
    queryFn: async () =>
      (await api.get<{ items: MenuItem[] }>(`/staff/pos/menu?location=${location}`)).data,
    refetchInterval: 60_000,
  });
  const recent = useQuery({
    queryKey: ['pos-recent', location],
    queryFn: async () =>
      (await api.get<{ items: Recent[] }>(`/staff/pos/orders?location=${location}`)).data.items,
    refetchInterval: 15_000,
  });

  const draft = useRef<Draft | null>(readDraft(location));
  const [lines, setLines] = useState<Line[]>([]);
  const [picking, setPicking] = useState<MenuItem | null>(null);
  const [slotId, setSlotId] = useState<string | null>(draft.current?.slotId ?? null);
  const [name, setName] = useState(draft.current?.name ?? '');
  const [phone, setPhone] = useState(draft.current?.phone ?? '');
  const [textUpdates, setTextUpdates] = useState(draft.current?.textUpdates ?? false);
  const [tipPct, setTipPct] = useState<number>(draft.current?.tipPct ?? 0);
  const [customer, setCustomer] = useState<FoundCustomer | null>(draft.current?.customer ?? null);
  const [earnRate, setEarnRate] = useState<number | null>(draft.current?.earnRate ?? null);
  const [useCredit, setUseCredit] = useState(draft.current?.useCredit ?? false);
  const [useBirthday, setUseBirthday] = useState(draft.current?.useBirthday ?? false);
  const [askPhone, setAskPhone] = useState(false);
  const [pickingTime, setPickingTime] = useState(false);
  const [showPhone, setShowPhone] = useState(false);
  const [readerId, setReaderId] = useState<string | null>(() => store.get(READER_KEY));
  const [active, setActive] = useState<string | null>(() => store.get(ACTIVE_KEY(location)));
  const [problem, setProblem] = useState<string | null>(null);
  const idem = useRef<string>(crypto.randomUUID());
  const ticketRef = useRef<HTMLElement>(null);

  useEffect(() => setActive(store.get(ACTIVE_KEY(location))), [location]);

  // The saved ticket comes back once the menu is loaded, so lines carry live prices and options.
  const restored = useRef(false);
  useEffect(() => {
    if (restored.current || !menu.data) return;
    restored.current = true;
    const saved = draft.current;
    if (!saved?.lines.length) return;
    const rebuilt: Line[] = [];
    for (const l of saved.lines) {
      const item = menu.data.items.find((i) => i.id === l.itemId);
      if (!item) continue;
      rebuilt.push({
        key: crypto.randomUUID(),
        item,
        quantity: l.quantity,
        modifierIds: l.modifierIds.filter((id) => item.modifiers.some((m) => m.id === id)),
        notes: l.notes,
      });
    }
    setLines(rebuilt);
  }, [menu.data]);

  // Keep the ticket on the device while it is being rung up.
  useEffect(() => {
    if (!restored.current) return;
    const empty = lines.length === 0 && !name.trim() && !phone.trim() && !customer;
    try {
      if (empty) localStorage.removeItem(DRAFT_KEY(location));
      else
        localStorage.setItem(
          DRAFT_KEY(location),
          JSON.stringify({
            lines: lines.map((l) => ({
              itemId: l.item.id,
              quantity: l.quantity,
              modifierIds: l.modifierIds,
              notes: l.notes,
            })),
            name,
            phone,
            textUpdates,
            tipPct,
            slotId,
            customer,
            earnRate,
            useCredit,
            useBirthday,
          } satisfies Draft),
        );
    } catch {
      // Private mode: the ticket lives only in this tab.
    }
  }, [
    location,
    lines,
    name,
    phone,
    textUpdates,
    tipPct,
    slotId,
    customer,
    earnRate,
    useCredit,
    useBirthday,
  ]);
  const openPayment = (id: string | null) => {
    store.set(ACTIVE_KEY(location), id);
    setActive(id);
  };

  const units = lines.reduce((s, l) => s + l.quantity, 0);
  const slots = setup.data?.slots ?? [];
  const slot =
    slots.find((s) => s.id === slotId && s.remaining >= units) ??
    slots.find((s) => s.remaining >= Math.max(units, 1));
  // Three times on the ticket; the rest open in a sheet, so the ticket stays short.
  const pickupChips = (() => {
    const first = slots.slice(0, 3);
    return slot && !first.some((s) => s.id === slot.id) ? [...first.slice(0, 2), slot] : first;
  })();
  const readers = setup.data?.readers ?? [];
  const reader =
    readers.find((r) => r.id === readerId) ??
    readers.find((r) => r.status === 'online') ??
    readers[0];

  const subtotal = lines.reduce((s, l) => {
    const mods = l.item.modifiers
      .filter((m) => l.modifierIds.includes(m.id))
      .reduce((a, m) => a + m.price_delta_cents, 0);
    return s + ((l.item.price_cents ?? 0) + mods) * l.quantity;
  }, 0);
  const tipCents = Math.round((subtotal * tipPct) / 100);
  const items = useMemo(
    () =>
      lines.map((l) => ({
        menu_item_id: l.item.id,
        quantity: l.quantity,
        modifier_ids: l.modifierIds,
        ...(l.notes.trim() ? { notes: l.notes.trim() } : {}),
      })),
    [lines],
  );
  const quote = useQuery({
    queryKey: ['pos-quote', location, items, tipCents, customer?.id, useCredit, useBirthday],
    queryFn: async () =>
      (
        await api.post<Quote>('/staff/pos/quote', {
          location,
          items,
          tip_cents: tipCents,
          ...(customer
            ? { customer_id: customer.id, use_credit: useCredit, apply_birthday: useBirthday }
            : {}),
        })
      ).data,
    enabled: items.length > 0,
    placeholderData: (prev) => prev,
  });

  const reset = () => {
    setLines([]);
    setName('');
    setPhone('');
    setTextUpdates(false);
    setTipPct(0);
    setCustomer(null);
    setUseCredit(false);
    setUseBirthday(false);
    setProblem(null);
    setSlotId(null);
    draft.current = null;
    store.set(DRAFT_KEY(location), null);
    idem.current = crypto.randomUUID();
  };

  const place = useMutation({
    mutationFn: async (pay: 'reader' | 'pay_link' | 'covered') =>
      (
        await api.post<PosStatus>(
          '/staff/pos/orders',
          {
            location,
            items,
            ...(slot ? { slot_id: slot.id } : {}),
            name: name.trim(),
            ...(phone.trim() ? { phone: phone.trim() } : {}),
            text_updates: textUpdates,
            tip_cents: tipCents,
            ...(customer
              ? { customer_id: customer.id, use_credit: useCredit, apply_birthday: useBirthday }
              : {}),
            pay,
            device_token: store.get(DEVICE_KEY) ?? '',
            ...(pay === 'reader' && reader ? { reader_id: reader.id } : {}),
          },
          { idempotencyKey: idem.current },
        )
      ).data,
    onSuccess: (o) => {
      qc.setQueryData(['pos-order', o.order_id], o);
      openPayment(o.order_id);
      reset();
      void qc.invalidateQueries({ queryKey: ['pos-setup', location] });
      void qc.invalidateQueries({ queryKey: ['pos-recent', location] });
    },
    onError: (err) => {
      setProblem(errorText(err));
      // Removed in admin: register again so the next try goes through with a new POS number.
      if (err instanceof ApiError && /no longer registered/.test(err.message)) {
        store.set(DEVICE_KEY, null);
        void qc.invalidateQueries({ queryKey: ['pos-device', location] });
      }
    },
  });

  const addLine = (line: Omit<Line, 'key'>) => {
    setLines((ls) => [...ls, { ...line, key: crypto.randomUUID() }]);
    idem.current = crypto.randomUUID();
  };
  const changeQty = (key: string, delta: number) => {
    setLines((ls) =>
      ls.flatMap((l) =>
        l.key !== key
          ? [l]
          : l.quantity + delta <= 0
            ? []
            : [{ ...l, quantity: Math.min(l.quantity + delta, 20) }],
      ),
    );
    idem.current = crypto.randomUUID();
  };

  const categories = new Map<string, MenuItem[]>();
  for (const i of menu.data?.items ?? [])
    categories.set(i.category, [...(categories.get(i.category) ?? []), i]);

  // What the card is charged: the total less Crave Credit (the birthday pizza is already off it).
  const total = quote.data?.totals.amount_due_cents ?? subtotal + tipCents;
  const covered = Boolean(
    customer && quote.data && quote.data.totals.amount_due_cents === 0 && lines.length,
  );
  const pickCustomer = (c: FoundCustomer | null, rate: number | null) => {
    setCustomer(c);
    setEarnRate(rate);
    setUseCredit(Boolean(c && c.credit_cents > 0));
    setUseBirthday(Boolean(c?.birthday));
    if (c?.name && !name.trim()) setName(c.name);
    if (c?.phone && !phone.trim()) setPhone(c.phone);
    idem.current = crypto.randomUUID();
  };
  // No order until this iPad has its POS number, so every walk-up says where it was taken.
  const nameOk = name.trim().length > 0 && Boolean(device.data);
  // With no time listed (a day with no online hours), a walk-up still opens one for right now.
  const pickupOk = Boolean(slot) || setup.data?.walk_up_now === true;
  const canReader = Boolean(
    setup.data?.payments.connected && reader && pickupOk && nameOk && lines.length,
  );
  const canLink = Boolean(
    setup.data?.payments.connected &&
    setup.data.payments.texting &&
    pickupOk &&
    nameOk &&
    lines.length,
  );
  const usPhone = (v: string) => {
    const digits = v.replace(/\D/g, '');
    return digits.length === 10 || (digits.length === 11 && digits.startsWith('1'));
  };
  /** No number yet? Ask for it instead of leaving the button dead. */
  const textPayLink = () => {
    setProblem(null);
    if (usPhone(phone)) place.mutate('pay_link');
    else setAskPhone(true);
  };
  if (setup.data && !setup.data.pos_enabled)
    return (
      <main className="pos" style={{ gridTemplateColumns: 'minmax(0, 1fr)' }}>
        <style href="crave-pos" precedence="default">
          {CSS}
        </style>
        <h1 className="pos-title">New order</h1>
        <p className="pos-note" style={{ fontSize: 18 }}>
          Walk-up orders are turned off at {setup.data.location.name}. An owner or manager can turn
          them on in admin under Locations.
        </p>
      </main>
    );

  // Why a pay button is off, said plainly instead of leaving a grey button.
  const why = !lines.length
    ? 'Add something to the order.'
    : !pickupOk
      ? 'No pickup time is open right now.'
      : !device.data
        ? 'This iPad is registering as a POS.'
        : !name.trim()
          ? 'Enter a name for the order.'
          : !setup.data?.payments.connected
            ? 'Card payments are not set up. Connect Stripe in admin Integrations.'
            : null;
  const readerWhy = canReader
    ? null
    : (why ??
      (readers.length === 0
        ? 'no card reader is paired yet. A manager adds one in admin under POS & card readers.'
        : 'this reader is not ready.'));
  const linkWhy = canLink
    ? null
    : (why ??
      (!setup.data?.payments.texting
        ? 'texting is not connected. Connect Twilio in admin Integrations.'
        : null));

  return (
    <>
      <style href="crave-pos" precedence="default">
        {CSS}
      </style>
      <main className="pos">
        <div className="pos-col">
          <div className="pos-head">
            <h1 className="pos-title">New order</h1>
            <span className="pos-who" aria-label="This POS and who is signed in">
              {device.data ? device.data.label : device.isError ? 'POS not registered' : 'POS…'}
              {staffName ? ` · ${staffName}` : ''}
            </span>
          </div>
          {device.isError && (
            <p className="pos-error">
              This iPad could not register as a POS. {errorText(device.error)}
            </p>
          )}
          {(setup.isError || menu.isError) && (
            <p className="pos-error">{errorText(setup.error ?? menu.error)}</p>
          )}
          {setup.data && !setup.data.ready && (
            <p className="pos-error">
              This location has no sales tax rate yet. Set it in admin first.
            </p>
          )}
          {menu.isPending && <p className="pos-note">Loading the menu…</p>}
          {[...categories.entries()].map(([cat, list]) => (
            <section key={cat} style={{ display: 'grid', gap: 14 }}>
              <h2 className="pos-caption">{cat}</h2>
              <div className="pos-menu">
                {list.map((i) => (
                  <button
                    key={i.id}
                    type="button"
                    className="pos-item"
                    disabled={!i.orderable}
                    onClick={() =>
                      i.modifiers.length
                        ? setPicking(i)
                        : addLine({ item: i, quantity: 1, modifierIds: [], notes: '' })
                    }
                  >
                    <strong>
                      {i.name}
                      {i.spicy && (
                        <>
                          {' '}
                          <Spicy size="sm" />
                        </>
                      )}
                    </strong>
                    {i.unavailable ? (
                      <span className="pos-tag">Off the menu today</span>
                    ) : i.sold_out ? (
                      <span className="pos-tag">Low stock</span>
                    ) : null}
                    <span>{i.price_cents === null ? 'No price yet' : money(i.price_cents)}</span>
                  </button>
                ))}
              </div>
            </section>
          ))}

          {(recent.data?.length ?? 0) > 0 && (
            <section style={{ display: 'grid', gap: 14 }}>
              <h2 className="pos-caption">Earlier today</h2>
              <div className="pos-card pos-recent">
                {recent.data!.slice(0, 5).map((o) => (
                  <button key={o.id} type="button" onClick={() => openPayment(o.id)}>
                    <b>#{o.order_number.slice(-2)}</b>
                    <span
                      style={{
                        minWidth: 0,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {o.customer_name ?? 'Walk-up'} · {o.time_label} · {money(o.total_cents)}
                      {o.pos_number ? ` · POS ${o.pos_number}` : ''}
                      {o.taken_by ? ` · ${o.taken_by}` : ''}
                    </span>
                    <span
                      className="pos-pill"
                      data-tone={o.paid ? 'ok' : o.status === 'CANCELED' ? undefined : 'wait'}
                    >
                      {o.paid ? 'Paid' : o.status === 'CANCELED' ? 'Canceled' : 'Not paid'}
                    </span>
                  </button>
                ))}
              </div>
            </section>
          )}
        </div>

        <aside className="pos-card pos-ticket" ref={ticketRef} aria-label="Order">
          <div className="pos-ticket-head">
            <h2 style={{ margin: 0, fontSize: 22 }}>Order</h2>
            {lines.length === 0 ? (
              <p className="pos-note">Tap a pizza to add it.</p>
            ) : (
              <div className="pos-lines">
                {lines.map((l) => {
                  const mods = l.item.modifiers.filter((m) => l.modifierIds.includes(m.id));
                  const each =
                    (l.item.price_cents ?? 0) + mods.reduce((a, m) => a + m.price_delta_cents, 0);
                  return (
                    <div className="pos-line" key={l.key}>
                      <b>
                        {l.item.name}
                        {(l.item.spicy || mods.some((m) => m.makes_spicy)) && (
                          <>
                            {' '}
                            <Spicy size="sm" />
                          </>
                        )}
                      </b>
                      <b>{money(each * l.quantity)}</b>
                      {(mods.length > 0 || l.notes) && (
                        <small>
                          {[...mods.map((m) => m.name), l.notes && `“${l.notes}”`]
                            .filter(Boolean)
                            .join(' · ')}
                        </small>
                      )}
                      <div className="pos-line-actions">
                        <span className="pos-step">
                          <button
                            type="button"
                            aria-label={`One less ${l.item.name}`}
                            onClick={() => changeQty(l.key, -1)}
                          >
                            −
                          </button>
                          <span aria-live="polite">{l.quantity}</span>
                          <button
                            type="button"
                            aria-label={`One more ${l.item.name}`}
                            onClick={() => changeQty(l.key, 1)}
                          >
                            +
                          </button>
                        </span>
                        <button
                          type="button"
                          className="pos-link"
                          onClick={() => changeQty(l.key, -l.quantity)}
                        >
                          Remove
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
          <div className="pos-ticket-scroll">
            <CustomerFinder
              location={location}
              customer={customer}
              earnRate={earnRate}
              useCredit={useCredit}
              useBirthday={useBirthday}
              quote={quote.data}
              onPick={pickCustomer}
              onCredit={(v) => {
                setUseCredit(v);
                idem.current = crypto.randomUUID();
              }}
              onBirthday={(v) => {
                setUseBirthday(v);
                idem.current = crypto.randomUUID();
              }}
            />

            <div style={{ display: 'grid', gap: 8 }}>
              <span className="pos-field">Pickup</span>
              <div className="pos-seg">
                {slots.length === 0 && (
                  <button type="button" className="pos-chip" aria-pressed disabled>
                    Now
                  </button>
                )}
                {pickupChips.map((s, i) => (
                  <button
                    key={s.id}
                    type="button"
                    className="pos-chip"
                    aria-pressed={slot?.id === s.id}
                    disabled={s.remaining < Math.max(units, 1)}
                    onClick={() => setSlotId(s.id)}
                  >
                    {i === 0 && s.now ? 'Now' : s.label}
                  </button>
                ))}
                {slots.length > pickupChips.length && (
                  <button type="button" className="pos-chip" onClick={() => setPickingTime(true)}>
                    Later…
                  </button>
                )}
              </div>
              {slots.length === 0 && (
                <p className="pos-note">
                  No online pickup times today, so this walk-up is handed over at the counter as
                  soon as it’s made.
                </p>
              )}
            </div>
          </div>

          <div className="pos-ticket-foot">
            <label className="pos-field">
              Name
              <input
                className="pos-input"
                value={name}
                onChange={(e) => setName(e.target.value)}
                autoComplete="off"
                placeholder="Name for the order"
              />
            </label>
            {showPhone || phone.trim() ? (
              <label className="pos-field">
                Mobile (for a text when it’s ready)
                <input
                  className="pos-input"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  inputMode="tel"
                  autoComplete="off"
                  placeholder="(863) 555-0100"
                />
              </label>
            ) : (
              <button
                type="button"
                className="pos-link"
                style={{ justifySelf: 'start' }}
                onClick={() => setShowPhone(true)}
              >
                Add a mobile number
              </button>
            )}
            {phone.trim() && (
              <label className="pos-check">
                <input
                  type="checkbox"
                  checked={textUpdates}
                  onChange={(e) => setTextUpdates(e.target.checked)}
                />
                Text them when it’s ready
              </label>
            )}
            <div style={{ display: 'grid', gap: 6 }}>
              <span className="pos-field">Tip</span>
              <div className="pos-tips">
                {TIPS.map((t) => (
                  <button
                    key={t}
                    type="button"
                    className="pos-chip"
                    aria-pressed={tipPct === t}
                    onClick={() => setTipPct(t)}
                  >
                    {t === 0 ? 'No tip' : `${t}%`}
                    {t > 0 && subtotal > 0 && (
                      <small>{money(Math.round((subtotal * t) / 100))}</small>
                    )}
                  </button>
                ))}
              </div>
            </div>
            <div className="pos-totals" aria-live="polite">
              <div>
                <span>Subtotal</span>
                <span>{money(quote.data?.totals.subtotal_cents ?? subtotal)}</span>
              </div>
              <div>
                <span>Tax</span>
                <span>{quote.data ? money(quote.data.totals.tax_cents) : '—'}</span>
              </div>
              {tipCents > 0 && (
                <div>
                  <span>Tip</span>
                  <span>{money(tipCents)}</span>
                </div>
              )}
              {(quote.data?.totals.discount_cents ?? 0) > 0 && (
                <div>
                  <span>Birthday pizza</span>
                  <span>−{money(quote.data!.totals.discount_cents)}</span>
                </div>
              )}
              {(quote.data?.totals.credit_applied_cents ?? 0) > 0 && (
                <div>
                  <span>Crave Credit</span>
                  <span>−{money(quote.data!.totals.credit_applied_cents)}</span>
                </div>
              )}
              <div className="pos-total">
                <span>
                  {(quote.data?.totals.credit_applied_cents ?? 0) > 0 ? 'To pay' : 'Total'}
                </span>
                <span>{lines.length ? money(total) : '$0.00'}</span>
              </div>
            </div>

            {problem && (
              <p className="pos-error" role="alert">
                {problem}
              </p>
            )}
            {why && lines.length > 0 && <p className="pos-note">{why}</p>}

            {lines.length > 0 && (
              <button
                type="button"
                className="pos-link"
                style={{ justifySelf: 'end', padding: 0 }}
                onClick={() => {
                  if (window.confirm('Clear this order and start over?')) reset();
                }}
              >
                Clear order
              </button>
            )}
            <div className="pos-pay">
              {covered && (
                <button
                  type="button"
                  className="pos-btn"
                  disabled={!slot || !nameOk || place.isPending}
                  onClick={() => {
                    setProblem(null);
                    place.mutate('covered');
                  }}
                >
                  {place.isPending && place.variables === 'covered'
                    ? 'Placing…'
                    : 'Place order, paid with credit'}
                </button>
              )}
              {!covered && readers.length > 1 && (
                <div className="pos-seg">
                  {readers.map((r) => (
                    <button
                      key={r.id}
                      type="button"
                      className="pos-chip"
                      aria-pressed={reader?.id === r.id}
                      onClick={() => {
                        store.set(READER_KEY, r.id);
                        setReaderId(r.id);
                      }}
                    >
                      {r.label}
                      <small>{r.status === 'online' ? 'Ready' : 'Offline'}</small>
                    </button>
                  ))}
                </div>
              )}
              {!covered && (
                <>
                  <button
                    type="button"
                    className="pos-btn"
                    disabled={!canReader || place.isPending}
                    onClick={() => {
                      setProblem(null);
                      place.mutate('reader');
                    }}
                  >
                    {place.isPending && place.variables === 'reader'
                      ? 'Starting…'
                      : readers.length === 0
                        ? 'No card reader yet'
                        : `Charge ${lines.length ? money(total) : ''} on ${reader?.label ?? 'reader'}`}
                  </button>
                  <button
                    type="button"
                    className="pos-btn"
                    data-kind="secondary"
                    disabled={!canLink || place.isPending}
                    onClick={textPayLink}
                  >
                    {place.isPending && place.variables === 'pay_link'
                      ? 'Sending…'
                      : 'Text a pay link'}
                  </button>
                </>
              )}
              {!covered && readerWhy && <p className="pos-note">Charge on a reader: {readerWhy}</p>}
              {!covered && linkWhy && <p className="pos-note">Text a pay link: {linkWhy}</p>}
            </div>
          </div>
        </aside>
      </main>

      {lines.length > 0 && (
        <div className="pos-bar">
          <button
            type="button"
            className="pos-btn"
            onClick={() => ticketRef.current?.scrollIntoView({ behavior: 'smooth' })}
          >
            Review order · {units} · {money(total)}
          </button>
        </div>
      )}

      <Sheet
        open={pickingTime}
        title="Pickup time"
        onClose={() => setPickingTime(false)}
        closeLabel="Cancel"
      >
        <div className="pos-card pos-mods">
          {slots.map((s, i) => (
            <label className="pos-mod" key={s.id}>
              <span>
                {i === 0 && s.now ? 'Now' : s.label}
                <span className="pos-note">
                  {s.remaining} {s.remaining === 1 ? 'pizza' : 'pizzas'} left
                </span>
              </span>
              <input
                type="radio"
                name="pos-pickup"
                checked={slot?.id === s.id}
                disabled={s.remaining < Math.max(units, 1)}
                onChange={() => {
                  setSlotId(s.id);
                  setPickingTime(false);
                }}
              />
            </label>
          ))}
        </div>
      </Sheet>

      <Sheet
        open={askPhone}
        title="Text the pay link"
        onClose={() => setAskPhone(false)}
        closeLabel="Cancel"
      >
        <form
          style={{ display: 'grid', gap: 16 }}
          onSubmit={(e) => {
            e.preventDefault();
            if (!usPhone(phone)) return;
            setAskPhone(false);
            place.mutate('pay_link');
          }}
        >
          <p className="pos-note">
            We’ll text them a link to pay {money(total)} on their own phone, with Apple Pay or a
            card. The order waits until they pay.
          </p>
          <label className="pos-field">
            Their mobile number
            <input
              className="pos-input"
              value={phone}
              autoFocus
              inputMode="tel"
              autoComplete="off"
              placeholder="(863) 555-0100"
              onChange={(e) => setPhone(e.target.value)}
            />
          </label>
          <button type="submit" className="pos-btn" disabled={!usPhone(phone) || place.isPending}>
            {place.isPending ? 'Sending…' : 'Send the link'}
          </button>
        </form>
      </Sheet>

      <Sheet
        open={Boolean(picking)}
        title={picking?.name ?? ''}
        onClose={() => setPicking(null)}
        closeLabel="Cancel"
      >
        {picking && (
          <ItemPicker
            item={picking}
            onAdd={(line) => {
              addLine(line);
              setPicking(null);
            }}
          />
        )}
      </Sheet>

      {active && (
        <PaymentSheet
          orderId={active}
          setup={setup.data}
          onClose={() => {
            openPayment(null);
            void qc.invalidateQueries({ queryKey: ['pos-recent', location] });
            void qc.invalidateQueries({ queryKey: ['pos-setup', location] });
          }}
        />
      )}
    </>
  );
}

function CustomerFinder({
  location,
  customer,
  earnRate,
  useCredit,
  useBirthday,
  quote,
  onPick,
  onCredit,
  onBirthday,
}: {
  location: string;
  customer: FoundCustomer | null;
  earnRate: number | null;
  useCredit: boolean;
  useBirthday: boolean;
  quote: Quote | undefined;
  onPick: (c: FoundCustomer | null, earnRate: number | null) => void;
  onCredit: (v: boolean) => void;
  onBirthday: (v: boolean) => void;
}) {
  const [q, setQ] = useState('');
  const [open, setOpen] = useState(false);
  const find = useMutation({
    mutationFn: async () =>
      (
        await api.get<Lookup>(
          `/staff/pos/customers?location=${encodeURIComponent(location)}&q=${encodeURIComponent(q.trim())}`,
        )
      ).data,
    onSuccess: (r) => {
      if (r.items.length === 1) {
        onPick(r.items[0]!, r.loyalty.earn_rate_bps);
        setOpen(false);
        setQ('');
      }
    },
  });

  if (customer) {
    const paidFood = quote
      ? Math.max(
          0,
          quote.totals.subtotal_cents -
            quote.totals.discount_cents -
            quote.totals.credit_applied_cents,
        )
      : 0;
    const earns = customer.has_account && earnRate ? Math.floor((paidFood * earnRate) / 10_000) : 0;
    return (
      <div className="pos-cust">
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            gap: 8,
            alignItems: 'flex-start',
          }}
        >
          <div style={{ minWidth: 0 }}>
            <b style={{ fontSize: 18 }}>{customer.name ?? 'Customer'}</b>
            <div className="pos-note">
              {[customer.phone, customer.email_hint].filter(Boolean).join(' · ')}
            </div>
          </div>
          <button type="button" className="pos-link" onClick={() => onPick(null, null)}>
            Remove
          </button>
        </div>
        {customer.has_account ? (
          <>
            {customer.credit_cents > 0 && (
              <label className="pos-check">
                <input
                  type="checkbox"
                  checked={useCredit}
                  onChange={(e) => onCredit(e.target.checked)}
                />
                Use Crave Credit ({money(customer.credit_cents)})
              </label>
            )}
            {customer.birthday && (
              <label className="pos-check">
                <input
                  type="checkbox"
                  checked={useBirthday}
                  onChange={(e) => onBirthday(e.target.checked)}
                />
                Birthday pizza
                {customer.birthday.item_name ? ` (${customer.birthday.item_name})` : ''}
              </label>
            )}
            {customer.birthday && useBirthday && quote && !quote.birthday && (
              <p className="pos-note">
                Add {customer.birthday.item_name ?? 'a pizza'} to use the birthday pizza.
              </p>
            )}
            {earns > 0 && (
              <p className="pos-note">Earns about {money(earns)} in Crave Credit once picked up.</p>
            )}
            {customer.credit_cents === 0 && !customer.birthday && earns === 0 && (
              <p className="pos-note">No Crave Credit yet.</p>
            )}
          </>
        ) : (
          <p className="pos-note">
            No Crave account yet, so this order won’t earn credit. They can sign up at
            order.cravepizzafl.com with this email to start earning.
          </p>
        )}
      </div>
    );
  }

  if (!open)
    return (
      <button
        type="button"
        className="pos-link"
        style={{ justifySelf: 'start', padding: 0, minHeight: 32 }}
        onClick={() => setOpen(true)}
      >
        Find customer · Crave Credit
      </button>
    );

  const results = find.data?.items ?? [];
  return (
    <form
      className="pos-cust"
      onSubmit={(e) => {
        e.preventDefault();
        if (q.trim()) find.mutate();
      }}
    >
      <label className="pos-field">
        Customer’s mobile number or email
        <div style={{ display: 'flex', gap: 8 }}>
          <input
            className="pos-input"
            value={q}
            autoFocus
            autoComplete="off"
            autoCapitalize="off"
            inputMode="email"
            placeholder="(863) 555-0100 or name@email.com"
            onChange={(e) => setQ(e.target.value)}
          />
          <button type="submit" className="pos-chip" disabled={!q.trim() || find.isPending}>
            {find.isPending ? 'Finding…' : 'Find'}
          </button>
        </div>
      </label>
      {find.isError && <p className="pos-error">{errorText(find.error)}</p>}
      {find.isSuccess && results.length === 0 && (
        <p className="pos-note">
          No customer with that number or email. You can still take the order.
        </p>
      )}
      {results.length > 1 &&
        results.map((c) => (
          <button
            key={c.id}
            type="button"
            className="pos-chip"
            style={{ justifyContent: 'space-between', display: 'flex' }}
            onClick={() => {
              onPick(c, find.data!.loyalty.earn_rate_bps);
              setOpen(false);
              setQ('');
            }}
          >
            {c.name ?? c.email_hint}
            <small>{c.has_account ? money(c.credit_cents) : 'Guest'}</small>
          </button>
        ))}
      <button
        type="button"
        className="pos-link"
        style={{ justifySelf: 'start' }}
        onClick={() => setOpen(false)}
      >
        Cancel
      </button>
    </form>
  );
}

function ItemPicker({ item, onAdd }: { item: MenuItem; onAdd: (line: Omit<Line, 'key'>) => void }) {
  const [quantity, setQuantity] = useState(1);
  const [ids, setIds] = useState<string[]>([]);
  const [notes, setNotes] = useState('');
  const each =
    (item.price_cents ?? 0) +
    item.modifiers.filter((m) => ids.includes(m.id)).reduce((a, m) => a + m.price_delta_cents, 0);
  return (
    <>
      <div className="pos-card pos-mods">
        {item.modifiers.map((m) => (
          <label className="pos-mod" key={m.id}>
            <span>
              {m.name}
              {m.makes_spicy && (
                <>
                  {' '}
                  <Spicy size="sm" />
                </>
              )}
              {m.price_delta_cents > 0 && (
                <span style={{ color: 'var(--ink-soft)', fontWeight: 500 }}>
                  {' '}
                  +{money(m.price_delta_cents)}
                </span>
              )}
            </span>
            <input
              type="checkbox"
              checked={ids.includes(m.id)}
              onChange={(e) =>
                setIds((cur) => (e.target.checked ? [...cur, m.id] : cur.filter((x) => x !== m.id)))
              }
            />
          </label>
        ))}
      </div>
      <label className="pos-field">
        Note for the kitchen <span style={{ fontWeight: 500 }}>(optional)</span>
        <input
          className="pos-input"
          value={notes}
          maxLength={200}
          onChange={(e) => setNotes(e.target.value)}
          placeholder="Well done, cut in 6"
        />
      </label>
      <div
        style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12 }}
      >
        <span className="pos-step">
          <button
            type="button"
            aria-label="One less"
            onClick={() => setQuantity((q) => Math.max(1, q - 1))}
          >
            −
          </button>
          <span aria-live="polite">{quantity}</span>
          <button
            type="button"
            aria-label="One more"
            onClick={() => setQuantity((q) => Math.min(20, q + 1))}
          >
            +
          </button>
        </span>
        <button
          type="button"
          className="pos-btn"
          style={{ flex: 1 }}
          onClick={() => onAdd({ item, quantity, modifierIds: ids, notes })}
        >
          Add · {money(each * quantity)}
        </button>
      </div>
    </>
  );
}

function PaymentSheet({
  orderId,
  setup,
  onClose,
}: {
  orderId: string;
  setup: Setup | undefined;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const [problem, setProblem] = useState<string | null>(null);
  const status = useQuery({
    queryKey: ['pos-order', orderId],
    queryFn: async () => (await api.get<PosStatus>(`/staff/pos/orders/${orderId}`)).data,
    refetchInterval: (q) =>
      q.state.data && (q.state.data.paid || q.state.data.canceled) ? false : 2000,
  });
  const act = useMutation({
    mutationFn: async (a: { path: string; body?: unknown }) =>
      (await api.post<PosStatus>(`/staff/pos/orders/${orderId}/${a.path}`, a.body ?? {})).data,
    onMutate: () => setProblem(null),
    onSuccess: (o) => {
      qc.setQueryData(['pos-order', orderId], (prev: PosStatus | undefined) => ({ ...prev, ...o }));
      if (o.reader_problem) setProblem(o.reader_problem);
    },
    onError: (err) => setProblem(errorText(err)),
  });
  const o = status.data;
  const readers = setup?.readers ?? [];
  const reader = readers.find((r) => r.id === o?.reader?.id) ?? readers[0];
  const waitingOnReader = o?.collected_by === 'reader' && o.reader_action?.status === 'in_progress';
  const simulated = Boolean(
    setup?.payments.test_mode && readers.find((r) => r.id === o?.reader?.id)?.simulated,
  );

  // A paid order clears itself after a moment, ready for the next customer.
  useEffect(() => {
    if (!o?.paid) return;
    const t = setTimeout(onClose, 6000);
    return () => clearTimeout(t);
  }, [o?.paid, onClose]);

  const title = o ? `Order #${o.order_number.slice(-2)}` : 'Payment';
  return (
    <Sheet
      open
      title={title}
      onClose={onClose}
      closeLabel={o?.paid ? 'Done' : waitingOnReader ? 'Leave for later' : 'Close'}
    >
      {!o ? (
        <div className="pos-status">
          {status.isError ? (
            <p className="pos-error">{errorText(status.error)}</p>
          ) : (
            <div className="pos-spinner" aria-label="Loading" />
          )}
        </div>
      ) : o.paid ? (
        <div className="pos-status" role="status">
          <div className="pos-done" aria-hidden="true">
            <svg width="40" height="40" viewBox="0 0 24 24">
              <path
                d="M5 12.5l4.5 4.5L19 7.5"
                fill="none"
                stroke="#fff"
                strokeWidth="2.8"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </div>
          <div className="pos-amount">{money(o.total_cents)}</div>
          <div className="pos-state">Paid. Sent to the kitchen.</div>
          <p className="pos-note">
            {o.order_number}
            {o.customer_name ? ` for ${o.customer_name}` : ''}
            {o.pos ? ` · Taken on ${o.pos}${o.taken_by ? ` by ${o.taken_by}` : ''}` : ''}
          </p>
          {problem && (
            <p className="pos-error" role="alert">
              {problem}
            </p>
          )}
          <button type="button" className="pos-btn" style={{ width: '100%' }} onClick={onClose}>
            New order
          </button>
          {o.can_void && (
            <button
              type="button"
              className="pos-btn"
              data-kind="plain"
              disabled={act.isPending}
              onClick={() => {
                if (
                  window.confirm(
                    `Void order ${o.order_number}? The card was only held, never charged, so nothing is refunded. The pizzas leave the kitchen queue.`,
                  )
                )
                  act.mutate({ path: 'cancel' });
              }}
            >
              {act.isPending ? 'Voiding…' : 'Void this order'}
            </button>
          )}
        </div>
      ) : o.canceled ? (
        <div className="pos-status" role="status">
          <div className="pos-state">Order canceled</div>
          <p className="pos-note">
            Nothing was charged. The pickup time, the stock and any Crave Credit are free again.
          </p>
          <button type="button" className="pos-btn" style={{ width: '100%' }} onClick={onClose}>
            New order
          </button>
        </div>
      ) : (
        <>
          <div className="pos-status" role="status" aria-live="polite">
            <div className="pos-amount">{money(o.total_cents)}</div>
            {o.collected_by === 'pay_link' ? (
              <>
                <div className="pos-state">Waiting for them to pay by link</div>
                <p className="pos-note">
                  {o.text?.status === 'failed'
                    ? `The text didn’t go through${o.text.error ? `: ${o.text.error}` : ''}. They can scan this instead.`
                    : `Texted${o.pay_phone ? ` to ${o.pay_phone}` : ''}. They can also scan this to pay now.`}
                </p>
                {o.pay_url && <QrCode value={o.pay_url} size={200} />}
              </>
            ) : waitingOnReader ? (
              <>
                <div className="pos-spinner" aria-hidden="true" />
                <div className="pos-state">
                  Tap, insert or swipe on {o.reader?.label ?? 'the reader'}
                </div>
              </>
            ) : (
              <>
                <div className="pos-state">{o.last_error ? 'Not paid' : 'Ready to charge'}</div>
                {o.last_error && <p className="pos-error">{o.last_error}</p>}
              </>
            )}
          </div>
          {problem && (
            <p className="pos-error" role="alert">
              {problem}
            </p>
          )}

          {simulated && waitingOnReader && (
            <div className="pos-test">
              <span className="pos-note" style={{ width: '100%', textAlign: 'center' }}>
                Test mode
              </span>
              <button
                type="button"
                className="pos-chip"
                disabled={act.isPending}
                onClick={() => act.mutate({ path: 'test-card', body: { declined: false } })}
              >
                Approve test card
              </button>
              <button
                type="button"
                className="pos-chip"
                disabled={act.isPending}
                onClick={() => act.mutate({ path: 'test-card', body: { declined: true } })}
              >
                Decline test card
              </button>
            </div>
          )}

          <div className="pos-pay">
            {o.collected_by === 'reader' && !waitingOnReader && reader && (
              <button
                type="button"
                className="pos-btn"
                disabled={act.isPending}
                onClick={() => act.mutate({ path: 'charge', body: { reader_id: reader.id } })}
              >
                {o.last_error ? 'Try again' : 'Charge'} on {reader.label}
              </button>
            )}
            {waitingOnReader && (
              <button
                type="button"
                className="pos-btn"
                data-kind="secondary"
                disabled={act.isPending}
                onClick={() => act.mutate({ path: 'stop-reader' })}
              >
                Stop the reader
              </button>
            )}
            <button
              type="button"
              className="pos-btn"
              data-kind="plain"
              disabled={act.isPending}
              onClick={() => act.mutate({ path: 'cancel' })}
            >
              Cancel order
            </button>
          </div>
          <p className="pos-note" style={{ textAlign: 'center' }}>
            Nothing goes to the kitchen until it’s paid.
          </p>
        </>
      )}
    </Sheet>
  );
}
